import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Table, Alert, Button, ProgressBar } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { dashboardAPI } from '../services/api';

const Dashboard = () => {
    const [dashboardData, setDashboardData] = useState({
        totals: {
            users: 0,
            vendors: 0,
            stores: 0,
            products: 0,
            orders: 0,
            revenue: 0
        },
        by_store: [],
        by_vendor: [],
        by_user: []
    });
    const [userRole, setUserRole] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchDashboardData();
    }, []);

    const fetchDashboardData = async () => {
        try {
            const response = await dashboardAPI.getStats();
            console.log('Dashboard API Response:', response.data); // For debugging

            // Based on your API response, data is in response.data.data
            const apiData = response.data;

            if (apiData && apiData.status === 'success' && apiData.data) {
                setDashboardData(apiData.data);
                setUserRole(apiData.data.user_role || '');
            } else {
                // Fallback to demo data
                setError('Invalid data format received from server');
                setDemoData();
            }
        } catch (err) {
            console.error('Dashboard API error:', err);
            setError('Failed to fetch dashboard data. Please try again later.');
            setDemoData();
        } finally {
            setLoading(false);
        }
    };

    const setDemoData = () => {
        setDashboardData({
            totals: {
                users: 6,
                vendors: 6,
                stores: 2,
                products: 11,
                orders: 3,
                revenue: 5947.99
            },
            by_store: [
                {
                    "id": "store_69248d310a7e20.59467189",
                    "name": "Golf Cart Superstore",
                    "products": 1,
                    "orders": 1,
                    "revenue": "4999.99"
                },
                {
                    "id": "store_Q3A1gOlN0h-8DdZX",
                    "name": "Golf Cart Super",
                    "products": 1,
                    "orders": 1,
                    "revenue": "4999.99"
                }
            ],
            by_vendor: [
                {
                    "id": "vendor_1",
                    "name": "Acme Electronics lulu",
                    "products": 7,
                    "orders": 2,
                    "revenue": "5798.99"
                },
                {
                    "id": "vendor_2",
                    "name": "Home & Kitchen Co",
                    "products": 1,
                    "orders": 1,
                    "revenue": "149.00"
                },
                {
                    "id": "vendor_68c0c204ce4706.27124358",
                    "name": "Vendor C",
                    "products": 1,
                    "orders": 0,
                    "revenue": "0.00"
                },
                {
                    "id": "vendor_68c0d38ebbac78.64251231",
                    "name": "Vendor A",
                    "products": 1,
                    "orders": 0,
                    "revenue": "0.00"
                },
                {
                    "id": "vendor_7911895442bbac7d_mgpvg03m",
                    "name": "lulu2",
                    "products": 1,
                    "orders": 0,
                    "revenue": "0.00"
                },
                {
                    "id": "vendor_dbb7d34e44a1d9b0_miet97wz",
                    "name": "Golf Cart Specialists",
                    "products": 0,
                    "orders": 0,
                    "revenue": "0.00"
                }
            ],
            by_user: [
                {
                    "id": "user_2",
                    "username": "bob_edited",
                    "email": "bob_edited@example.com",
                    "orders": 1,
                    "revenue": "149.00"
                },
                {
                    "id": "user_68c0abc35ab425.41063157",
                    "username": "king",
                    "email": "king@gmail.com",
                    "orders": 0,
                    "revenue": "0.00"
                },
                {
                    "id": "user_68c0d16fd88c44.52077574",
                    "username": "jane",
                    "email": "jane@example.com",
                    "orders": 0,
                    "revenue": "0.00"
                },
                {
                    "id": "user_68ed7ee6b4ff96.24012877",
                    "username": "user_123@gmail.com",
                    "email": "user_123@gmail.com",
                    "orders": 0,
                    "revenue": "0.00"
                },
                {
                    "id": "user_6924e25a41100_Mwb8e1Kt",
                    "username": "golf_player",
                    "email": "golfer@gmail.com",
                    "orders": 0,
                    "revenue": "0.00"
                },
                {
                    "id": "user_69258a053a6ae_K2Y78lPY",
                    "username": "golf_enthusiast2",
                    "email": "golfer2@gmail.com",
                    "orders": 1,
                    "revenue": "4999.99"
                }
            ]
        });
    };

    const refreshData = () => {
        setLoading(true);
        setError('');
        fetchDashboardData();
    };

    const formatCurrency = (amount) => {
        const numAmount = typeof amount === 'string' ? parseFloat(amount) : (amount || 0);
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(numAmount);
    };

    // Safely access data with fallbacks
    const { totals = {}, by_store = [], by_vendor = [], by_user = [] } = dashboardData;

    if (loading) return (
        <div className="text-center py-5">
            <div className="spinner-border text-success" role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading dashboard data...</p>
        </div>
    );

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h1 className="mb-0">Dashboard Overview</h1>
                    <small className="text-muted">
                        Welcome! Your role: <span className="badge bg-info">{userRole || 'admin'}</span>
                    </small>
                </div>
                <div>
                    <Button variant="outline-success" onClick={refreshData}>
                        Refresh Data
                    </Button>
                </div>
            </div>

            {error && (
                <Alert variant="warning" dismissible onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {/* Main Stats Cards */}
            <Row className="g-4 mb-4">
                <Col md={2} sm={6}>
                    <Card className="h-100 border-0 shadow-sm bg-success text-white">
                        <Card.Body className="text-center">
                            <div className="h2 mb-2">👥</div>
                            <Card.Title>Users</Card.Title>
                            <h3>{totals.users || 0}</h3>
                            <small>Registered Users</small>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={2} sm={6}>
                    <Card className="h-100 border-0 shadow-sm bg-primary text-white">
                        <Card.Body className="text-center">
                            <div className="h2 mb-2">🏪</div>
                            <Card.Title>Stores</Card.Title>
                            <h3>{totals.stores || 0}</h3>
                            <small>Active Stores</small>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={2} sm={6}>
                    <Card className="h-100 border-0 shadow-sm bg-info text-white">
                        <Card.Body className="text-center">
                            <div className="h2 mb-2">🏢</div>
                            <Card.Title>Vendors</Card.Title>
                            <h3>{totals.vendors || 0}</h3>
                            <small>Total Vendors</small>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={2} sm={6}>
                    <Card className="h-100 border-0 shadow-sm bg-warning text-dark">
                        <Card.Body className="text-center">
                            <div className="h2 mb-2">📦</div>
                            <Card.Title>Products</Card.Title>
                            <h3>{totals.products || 0}</h3>
                            <small>Available Products</small>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={2} sm={6}>
                    <Card className="h-100 border-0 shadow-sm bg-secondary text-white">
                        <Card.Body className="text-center">
                            <div className="h2 mb-2">💰</div>
                            <Card.Title>Orders</Card.Title>
                            <h3>{totals.orders || 0}</h3>
                            <small>Total Orders</small>
                        </Card.Body>
                    </Card>
                </Col>

                <Col md={2} sm={6}>
                    <Card className="h-100 border-0 shadow-sm bg-dark text-white">
                        <Card.Body className="text-center">
                            <div className="h2 mb-2">💵</div>
                            <Card.Title>Revenue</Card.Title>
                            <h3>{formatCurrency(totals.revenue)}</h3>
                            <small>Total Revenue</small>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className="g-4">
                {/* Stores Performance - Show top 3 stores */}
                <Col lg={6}>
                    <Card className="border-0 shadow-sm h-100">
                        <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Top Performing Stores</h5>
                            <Link to="/stores" className="btn btn-sm btn-outline-success">
                                View All
                            </Link>
                        </Card.Header>
                        <Card.Body>
                            {by_store.length > 0 ? (
                                <div>
                                    {by_store.slice(0, 3).map((store, index) => (
                                        <div key={store.id} className="mb-3 p-3 border rounded">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <div className="d-flex align-items-center">
                                                    <span className="badge bg-secondary me-2">#{index + 1}</span>
                                                    <h6 className="mb-0">{store.name}</h6>
                                                </div>
                                                <span className="badge bg-success">
                                                    {formatCurrency(parseFloat(store.revenue))}
                                                </span>
                                            </div>
                                            <div className="d-flex justify-content-between small text-muted mb-2">
                                                <span>Products: {store.products || 0}</span>
                                                <span>Orders: {store.orders || 0}</span>
                                            </div>
                                            <ProgressBar
                                                now={store.orders || 0}
                                                max={Math.max(...by_store.map(s => s.orders || 0))}
                                                variant="success"
                                                style={{ height: '6px' }}
                                            />
                                        </div>
                                    ))}
                                    {by_store.length > 3 && (
                                        <div className="text-center mt-3">
                                            <small className="text-muted">
                                                + {by_store.length - 3} more stores
                                            </small>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center py-4 text-muted">
                                    No store data available
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                {/* Vendor Performance - Show top 3 vendors */}
                <Col lg={6}>
                    <Card className="border-0 shadow-sm h-100">
                        <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Top Performing Vendors</h5>
                            <Link to="/vendors" className="btn btn-sm btn-outline-primary">
                                View All
                            </Link>
                        </Card.Header>
                        <Card.Body>
                            {by_vendor.length > 0 ? (
                                <div>
                                    {by_vendor
                                        .filter(v => (v.orders || 0) > 0) // Only show vendors with orders
                                        .slice(0, 3)
                                        .map((vendor, index) => (
                                            <div key={vendor.id} className="mb-3 p-3 border rounded">
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <div className="d-flex align-items-center">
                                                        <span className="badge bg-secondary me-2">#{index + 1}</span>
                                                        <h6 className="mb-0">{vendor.name}</h6>
                                                    </div>
                                                    <span className="badge bg-primary">
                                                        {formatCurrency(parseFloat(vendor.revenue))}
                                                    </span>
                                                </div>
                                                <div className="d-flex justify-content-between small text-muted mb-2">
                                                    <span>Products: {vendor.products || 0}</span>
                                                    <span>Orders: {vendor.orders || 0}</span>
                                                </div>
                                                <ProgressBar
                                                    now={vendor.orders || 0}
                                                    max={Math.max(...by_vendor.map(v => v.orders || 0))}
                                                    variant="primary"
                                                    style={{ height: '6px' }}
                                                />
                                            </div>
                                        ))}
                                    {by_vendor.length > 3 && (
                                        <div className="text-center mt-3">
                                            <small className="text-muted">
                                                + {by_vendor.length - 3} more vendors
                                            </small>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-center py-4 text-muted">
                                    No vendor data available
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className="g-4 mt-2">
                {/* User Activity - Show top 5 users */}
                <Col lg={6}>
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Top Customers</h5>
                            <Link to="/users" className="btn btn-sm btn-outline-warning">
                                View All
                            </Link>
                        </Card.Header>
                        <Card.Body>
                            {by_user.length > 0 ? (
                                <Table responsive hover>
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>User</th>
                                            <th>Email</th>
                                            <th>Orders</th>
                                            <th>Spent</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {by_user
                                            .filter(user => (user.orders || 0) > 0) // Only show users with orders
                                            .slice(0, 5)
                                            .map((user, index) => (
                                                <tr key={user.id}>
                                                    <td>
                                                        <span className="badge bg-secondary">#{index + 1}</span>
                                                    </td>
                                                    <td>
                                                        <div className="fw-semibold">{user.username || 'Unknown'}</div>
                                                    </td>
                                                    <td>
                                                        <small className="text-muted">{user.email || 'No email'}</small>
                                                    </td>
                                                    <td>
                                                        <span className={`badge bg-${user.orders > 0 ? 'success' : 'secondary'}`}>
                                                            {user.orders || 0}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className="fw-semibold text-success">
                                                            {formatCurrency(parseFloat(user.revenue))}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        {by_user.filter(user => (user.orders || 0) > 0).length === 0 && (
                                            <tr>
                                                <td colSpan="5" className="text-center py-4 text-muted">
                                                    No customer orders yet
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            ) : (
                                <div className="text-center py-4 text-muted">
                                    No user data available
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                {/* Quick Actions & System Status */}
                <Col lg={6}>
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white">
                            <h5 className="mb-0">Quick Actions</h5>
                        </Card.Header>
                        <Card.Body>
                            <div className="d-grid gap-2">
                                <Link to="/products" className="btn btn-outline-success text-start">
                                    📦 Manage Products ({totals.products || 0})
                                </Link>
                                <Link to="/orders" className="btn btn-outline-primary text-start">
                                    📋 View Orders ({totals.orders || 0})
                                </Link>
                                <Link to="/users" className="btn btn-outline-warning text-start">
                                    👥 User Management ({totals.users || 0})
                                </Link>
                                <Link to="/inventory" className="btn btn-outline-info text-start">
                                    📊 Inventory Status
                                </Link>
                                <Link to="/vendors" className="btn btn-outline-secondary text-start">
                                    🏢 Vendor Management ({totals.vendors || 0})
                                </Link>
                                <Link to="/stores" className="btn btn-outline-dark text-start">
                                    🏪 Store Management ({totals.stores || 0})
                                </Link>
                            </div>
                        </Card.Body>
                    </Card>

                    <Card className="border-0 shadow-sm mt-4">
                        <Card.Header className="bg-white">
                            <h5 className="mb-0">System Status</h5>
                        </Card.Header>
                        <Card.Body>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <span>API Status</span>
                                <span className="badge bg-success">Online</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <span>Database</span>
                                <span className="badge bg-success">Connected</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <span>Active Users</span>
                                <span className="badge bg-info">{by_user.filter(u => u.orders > 0).length}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <span>Active Vendors</span>
                                <span className="badge bg-info">{by_vendor.filter(v => v.orders > 0).length}</span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                                <span>Last Updated</span>
                                <span>{new Date().toLocaleTimeString()}</span>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Summary Stats */}
            <Row className="g-4 mt-2">
                <Col md={4}>
                    <Card className="border-0 shadow-sm bg-light">
                        <Card.Body className="text-center">
                            <h6 className="text-muted">Average Order Value</h6>
                            <h4 className="text-success">
                                {formatCurrency(totals.orders > 0 ? totals.revenue / totals.orders : 0)}
                            </h4>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="border-0 shadow-sm bg-light">
                        <Card.Body className="text-center">
                            <h6 className="text-muted">Products per Store</h6>
                            <h4 className="text-primary">
                                {totals.stores > 0 ? (totals.products / totals.stores).toFixed(1) : '0.0'}
                            </h4>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="border-0 shadow-sm bg-light">
                        <Card.Body className="text-center">
                            <h6 className="text-muted">Customer Conversion</h6>
                            <h4 className="text-warning">
                                {totals.users > 0 ? ((by_user.filter(u => u.orders > 0).length / totals.users) * 100).toFixed(1) : 0}%
                            </h4>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Recent Activity Summary */}
            <Card className="border-0 shadow-sm mt-4">
                <Card.Header className="bg-white">
                    <h5 className="mb-0">Recent Activity Summary</h5>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={3} className="text-center">
                            <div className="h4 text-success mb-1">{totals.orders || 0}</div>
                            <small className="text-muted">Total Orders</small>
                        </Col>
                        <Col md={3} className="text-center">
                            <div className="h4 text-primary mb-1">{formatCurrency(totals.revenue)}</div>
                            <small className="text-muted">Total Revenue</small>
                        </Col>
                        <Col md={3} className="text-center">
                            <div className="h4 text-warning mb-1">{totals.products || 0}</div>
                            <small className="text-muted">Active Products</small>
                        </Col>
                        <Col md={3} className="text-center">
                            <div className="h4 text-info mb-1">{by_vendor.filter(v => v.orders > 0).length}</div>
                            <small className="text-muted">Active Vendors</small>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
        </div>
    );
};

export default Dashboard;